import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { bankStatementService } from "../end-points/bank-statement";
import { toast } from "@/hooks/use-toast";
import {
  ImportFileData,
  CreateTransactionData,
  UpdateTransactionData,
  BankStatementFilters,
} from "@/types/bank-statement";

export function useBankAccounts() {
  return useQuery({
    queryKey: ["bank-accounts"],
    queryFn: () => bankStatementService.fetchBankAccounts(),
  });
}

export function useStatements(filters?: BankStatementFilters) {
  return useQuery({
    queryKey: ["bank-statements", filters],
    queryFn: () => bankStatementService.fetchStatements(filters),
  });
}

export function useTransactionById(id: number) {
  return useQuery({
    queryKey: ["bank-statement", id],
    queryFn: () => bankStatementService.fetchTransactionById(id),
    enabled: !!id,
  });
}

export function useSummary(accountId?: number, movieId?: number) {
  return useQuery({
    queryKey: ["bank-statement-summary", accountId, movieId],
    queryFn: () => bankStatementService.fetchSummary(accountId, movieId),
  });
}

export function useImportFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ImportFileData) => bankStatementService.importFile(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["bank-statements"] });
      queryClient.invalidateQueries({ queryKey: ["bank-statement-summary"] });
      toast({
        title: "Arquivo importado com sucesso",
        description: `${response.imported} transações foram importadas.`,
      });
    },
    onError: () => {
      toast({
        title: "Erro ao importar arquivo",
        description: "Ocorreu um erro ao processar o arquivo. Tente novamente.",
        variant: "destructive",
      });
    },
  });
}

export function useCreateRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTransactionData) =>
      bankStatementService.createRelease(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank-statements"] });
      queryClient.invalidateQueries({ queryKey: ["bank-statement-summary"] });
      toast({
        title: "Lançamento criado com sucesso",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao criar lançamento",
        description: "Ocorreu um erro ao salvar o lançamento. Tente novamente.",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateTransactionData }) =>
      bankStatementService.updateRelease(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank-statements"] });
      queryClient.invalidateQueries({ queryKey: ["bank-statement-summary"] });
      toast({
        title: "Lançamento atualizado com sucesso",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao atualizar lançamento",
        description: "Ocorreu um erro ao salvar as alterações. Tente novamente.",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => bankStatementService.deleteRelease(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank-statements"] });
      queryClient.invalidateQueries({ queryKey: ["bank-statement-summary"] });
      toast({
        title: "Lançamento excluído com sucesso",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao excluir lançamento",
        description: "Ocorreu um erro ao excluir o lançamento. Tente novamente.",
        variant: "destructive",
      });
    },
  });
}

export function useExportTransactions() {
  return useMutation({
    mutationFn: ({
      filters,
      format,
    }: {
      filters?: BankStatementFilters;
      format?: "xlsx" | "pdf";
    }) => bankStatementService.exportTransactions(filters, format),
    onSuccess: (response, { format = "xlsx" }) => {
      // Create blob and download
      const blob = new Blob([response], {
        type:
          format === "xlsx"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `extrato-bancario-${new Date().toISOString().split("T")[0]}.${format === "xlsx" ? "csv" : "txt"}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast({
        title: "Exportação realizada com sucesso",
      });
    },
    onError: () => {
      toast({
        title: "Erro ao exportar",
        description: "Ocorreu um erro ao exportar os dados. Tente novamente.",
        variant: "destructive",
      });
    },
  });
}
